import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { answerDataGrant } from '../../../../../helpers/company-data.js';
import NSJobComponent from '../../../job.js';
import JobSchema from '../../../../../models/job-schema.js';
import type Company from '../../../../../models/company.js';
import type { DataGrant, DataSet } from '../../../../../models/company-data.js';

import '../../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/data-access/offer-answer.json';
import { jobSchema } from '../../../job-schema.js';

// A connected company offered to share some of its data: an admin accepts it into the Data area, or declines
@customElement(NSCompanyDataOfferAnswer.is)
export default class NSCompanyDataOfferAnswer extends NSJobComponent {
  static get is() {
    return 'ns-company-data-offer-answer';
  }

  @state()
  private __grant?: DataGrant;

  @state()
  private __grantor?: Company;

  // An offered set is readable as a set, not its records, until it's accepted
  @state()
  private __set?: DataSet;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { answered: { type: 'hidden' } } });
  }

  async connectedCallback() {
    try {
      await super.connectedCallback();
      if (!this._db) throw new Error('Unable to find db service');
      const grantId = this.job.inputValues.GRANT_ID;
      if (typeof grantId !== 'string' || !grantId) throw new Error('Missing required grant ID');
      this.__grant = await this._db.getById<DataGrant>('dataGrant', grantId);
      if (!this.__grant) throw new Error('Unable to find the offer');
      this.__grantor = await this._db.getById<Company>('company', this.__grant.grantorCompanyId);
      if (this.__grant.dataSetId) this.__set = await this._db.getById<DataSet>('dataSet', this.__grant.dataSetId);
    } catch (err: unknown) {
      this.failJob(err, 'Unexpected error occurred while loading the offer');
    }
  }

  private async __answer(decision: 'accept' | 'decline') {
    if (!this.__grant) return;
    try {
      this.loading(decision === 'accept' ? 'Accepting' : 'Declining');
      await answerDataGrant(this._auth?.token, { grantId: this.__grant.id, decision });
      this.loaded();
      // The lambda closes this job with the workflow, so the card only has to go
      this._jobComplete();
    } catch (err) {
      this.loaded();
      this.showError(err, 'Unable to answer the offer.');
    }
  }

  render() {
    const grant = this.__grant;
    const who = this.__grantor?.name ?? 'A connected company';
    const admin = this._directory?.can('DATA_SHARING_ACCEPT') ?? false;
    const what = grant?.dataRecordId
      ? `a record from ${this.__set?.name ?? 'a data set'}`
      : (this.__set?.name ?? 'a data set');
    const level = grant?.level === 'CONTRIBUTE' ? 'to read and add records to' : 'to read';
    if (grant && grant.status !== 'OFFERED') {
      return html`
        <ns-job-layout><p slot="intro">This offer has already been answered or withdrawn.</p></ns-job-layout>
      `;
    }
    return html`
      <ns-job-layout>
        <p slot="intro">${who} offered ${grant?.replacesGrantId ? 'more access: ' : ''}${what}, ${level}.</p>
        ${this.__set || grant?.note || !admin
          ? html`
              <div class="job-panel">
                ${this.__set
                  ? html`
                      <h3>${this.__set.name}</h3>
                    `
                  : nothing}
                ${this.__set?.description
                  ? html`
                      <p>${this.__set.description}</p>
                    `
                  : nothing}
                ${grant?.note
                  ? html`
                      <p class="note">
                        <strong>Their note:</strong>
                        ${grant.note}
                      </p>
                    `
                  : nothing}
                ${admin
                  ? nothing
                  : html`
                      <p>Only a company admin can answer this.</p>
                    `}
              </div>
            `
          : nothing}
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <p>If you accept, it appears under Data, Shared with us. Their private fields stay hidden.</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (this.__grant && this.__grant.status !== 'OFFERED') return nothing;
    const admin = this._directory?.can('DATA_SHARING_ACCEPT') ?? false;
    return html`
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?disabled=${!admin}
        @click=${() => this.__answer('decline')}
      >
        Decline
      </wa-button>
      <wa-button variant="brand" type="button" ?disabled=${!admin} @click=${() => this.__answer('accept')}>
        Accept
      </wa-button>
    `;
  }
}
